/**
 * サンプル管理のデータ正本（ローカル Zip / クラウド DB）— 排他。
 *
 * - 同時に両方は使わない（どちらか一方のみ）
 * - クラウド DB はログイン必須
 * - 未ログインは常にローカル
 * - ログイン中はいつでも切替可（localStorage に保持）
 */

import { isSampleZipConnected } from './sampleZipFile';

export type SampleDataSource = 'local' | 'db';

const PREF_KEY = 'sample-manager.dataSource';

let authenticated = false;
let preference: SampleDataSource = readStoredPreference();

type PrefListener = () => void;
const prefListeners = new Set<PrefListener>();

function notifyPref(): void {
  for (const listener of prefListeners) listener();
}

function readStoredPreference(): SampleDataSource {
  try {
    const v = localStorage.getItem(PREF_KEY);
    if (v === 'local' || v === 'db') return v;
  } catch {
    /* private mode */
  }
  return 'db';
}

function writeStoredPreference(next: SampleDataSource): void {
  try {
    localStorage.setItem(PREF_KEY, next);
  } catch {
    /* private mode */
  }
}

export function setSampleAuthForLocalMode(isAuthenticated: boolean): void {
  authenticated = !!isAuthenticated;
}

/** 実効の正本希望（ゲストは常に local） */
export function getSampleDataSourcePreference(): SampleDataSource {
  if (!authenticated) return 'local';
  return preference;
}

export function setSampleDataSourcePreference(next: SampleDataSource): void {
  if (!authenticated && next === 'db') {
    return;
  }
  preference = next;
  writeStoredPreference(next);
  notifyPref();
}

export function subscribeSampleDataSourcePreference(listener: PrefListener): () => void {
  prefListeners.add(listener);
  return () => prefListeners.delete(listener);
}

/**
 * ローカル Zip がデータの正本として稼働中か（接続済み）。
 * UI（共有ボタン非表示等）・権限用。
 */
export function isSampleLocalMode(): boolean {
  return getSampleDataSourcePreference() === 'local' && isSampleZipConnected();
}

/** 正本希望がローカルか（ゲート表示中も含む。ナビ絞り込み等） */
export function isSampleLocalPreferred(): boolean {
  return getSampleDataSourcePreference() === 'local';
}

/** ローカル希望だが未接続 → ゲート表示 */
export function isSampleZipGateRequired(): boolean {
  return isSampleLocalPreferred() && !isSampleZipConnected();
}

/** クラウド DB を正本として使うか（ログイン必須・排他） */
export function isSampleDbMode(): boolean {
  return authenticated && getSampleDataSourcePreference() === 'db';
}

/**
 * service 層の正本解決。混在禁止。
 * - local: Zip 未接続ならエラー（DB にフォールバックしない）
 * - db: 未ログインならエラー
 */
export function resolveSampleDataSource(): SampleDataSource {
  const pref = getSampleDataSourcePreference();
  if (pref === 'local') {
    if (!isSampleZipConnected()) {
      throw new Error('sample.local_zip_required');
    }
    return 'local';
  }
  if (!authenticated) {
    throw new Error('sample.db_requires_login');
  }
  return 'db';
}
